/**
 * Robust epoch extraction for the timestamp shapes the RPC client can hand us:
 * an Effect `DateTime.Utc` (carries `epochMillis`), an ISO string, a number, or a
 * `Date`. Avoids `new Date(dateTimeUtc)` which produces "Invalid Date".
 */
export function toEpochMs(value: unknown): number {
  if (value == null) return 0
  if (typeof value === "number") return value
  if (typeof value === "string") return new Date(value).getTime()
  if (value instanceof Date) return value.getTime()
  if (
    typeof value === "object" &&
    "epochMillis" in value &&
    typeof (value as { epochMillis: unknown }).epochMillis === "number"
  ) {
    return (value as { epochMillis: number }).epochMillis
  }
  return 0
}

const SHORT_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
]

/**
 * Absolute date in DD/Mon/YY form, e.g. "23/Jun/26". Built from local date parts
 * (the submitter's own timezone) via an explicit month array, so it never renders
 * the full month name the way some ICU builds do for `toLocaleString`.
 */
export function formatShortDate(value: unknown): string {
  const ms = toEpochMs(value)
  if (!ms) return ""
  const d = new Date(ms)
  const day = String(d.getDate()).padStart(2, "0")
  const month = SHORT_MONTHS[d.getMonth()]
  const year = String(d.getFullYear()).slice(-2)
  return `${day}/${month}/${year}`
}

/** Compact relative time ("just now", "5m ago", "Yesterday", "3d ago"). */
export function formatRelativeTime(value: unknown): string {
  const diffMs = Date.now() - toEpochMs(value)
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 1) return "just now"
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) return "Yesterday"
  if (diffDays < 30) return `${diffDays}d ago`
  const diffMonths = Math.floor(diffDays / 30)
  return `${diffMonths}mo ago`
}
